import { Button } from "@/components/ui/button";
import { normalizeGender } from "@/features/profile-completion/profile-completion";
import { useApp } from "@/hooks/use-app-context";
import { Icon, type IconName } from "@/lib/icons";
import { getGenders } from "@/lib/signup.constants";
import type { AuthUser } from "@/types/auth.types";
import { countryName, initialsOf, visibleRole } from "./profile-form";
import type { SettingsCopy } from "./settings-copy";
import { Chip } from "./settings-ui";

/** Who the person is: avatar, name, contact details and account status. */
export function IdentityCard({ user, copy, onEdit }: { user: AuthUser; copy: SettingsCopy; onEdit: () => void }) {
  const { t, language } = useApp();
  const role = visibleRole(user.role);
  const gender = getGenders(t).find((option) => option.value === (normalizeGender(user.gender) ?? user.gender))?.label ?? user.gender;
  const displayName = user.name || user.username;

  const details: { key: string; icon: IconName; label: string; value?: string }[] = [
    { key: "email", icon: "mail", label: t.email, value: user.email },
    { key: "phone", icon: "phone", label: t.settingsPhone, value: user.phoneNumber },
    { key: "country", icon: "globe", label: t.settingsCountry, value: countryName(user.country, language) },
    { key: "gender", icon: "user", label: t.settingsGender, value: gender },
  ];

  return (
    <section aria-labelledby="settings-identity" className="rounded-lg border border-border/60 bg-surface p-4 sm:p-5">
      <div className="flex items-start gap-4">
        <span
          aria-hidden="true"
          className="grid size-14 shrink-0 place-items-center rounded-full bg-brand/10 text-lg font-semibold text-brand"
        >
          {initialsOf(user)}
        </span>
        <div className="min-w-0 flex-1">
          <h2 id="settings-identity" className="truncate text-lg font-semibold text-foreground">
            {displayName}
          </h2>
          {user.name && user.username ? <p className="truncate text-sm text-muted-foreground">@{user.username}</p> : null}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {user.isActive === false ? (
              <Chip tone="danger">{copy.accountInactive}</Chip>
            ) : (
              <Chip tone="success">
                <Icon name="checkCircle" className="size-3" />
                {copy.accountActive}
              </Chip>
            )}
            {user.isPaidValidated ? <Chip tone="brand">{copy.paidValidated}</Chip> : null}
            {role ? <Chip>{copy.roles[role]}</Chip> : null}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onEdit} className="shrink-0">
          <Icon name="edit" />
          {copy.edit}
        </Button>
      </div>

      <dl className="mt-5 grid gap-x-6 gap-y-4 border-t border-border/60 pt-4 sm:grid-cols-2">
        {details.map((item) => (
          <div key={item.key} className="flex min-w-0 items-start gap-3">
            <Icon name={item.icon} className="mt-0.5 size-4 shrink-0 text-faint-foreground" />
            <div className="min-w-0">
              <dt className="text-xs text-muted-foreground">{item.label}</dt>
              <dd className={item.value ? "truncate text-sm font-medium text-foreground" : "text-sm text-faint-foreground"}>
                {item.value || copy.notProvided}
              </dd>
            </div>
          </div>
        ))}
      </dl>
    </section>
  );
}
